import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import SlidePanel from '../shared/SlidePanel';
import toast from '../shared/Toast';
import { SOCIAL_VISIBLE } from '../../config/featureFlags';
import { saveFile } from '../../_utils/fileExport';
import { hapticSuccess, hapticTick } from '../../_utils/haptics';
import { headlineOf, termsOf, termsShort } from './data/codes';
import { linkFor, businessName } from './links';
import { Icon } from './ui';

/**
 * Share a code: the code itself, its link (which applies it on the booking
 * page), a QR poster for the salon, and straight into a post, an email or a
 * text. Every way out carries the small print with it.
 */

const INK = '#29282D';
const FONT = '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';
const loadQr = () => import(/* webpackChunkName: "qrcode" */ 'qrcode').then((m) => m.default || m);

// Wrap text to a width on a canvas, returning the lines
const wrap = (ctx, text, width) => {
  const words = String(text || '').split(/\s+/);
  const lines = [];
  let line = '';
  words.forEach((w) => {
    const next = line ? `${line} ${w}` : w;
    if (ctx.measureText(next).width > width && line) { lines.push(line); line = w; } else line = next;
  });
  if (line) lines.push(line);
  return lines;
};

// A print-ready poster: the offer, the code, a QR that opens the booking page
// with the code applied, and the small print in full.
async function posterBlob({ url, code, name }) {
  const QR = await loadQr();
  const W = 1200;
  const H = 1700;
  const SIZE = 760;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);
  ctx.textAlign = 'center';
  ctx.fillStyle = INK;
  ctx.font = `800 92px ${FONT}`;
  wrap(ctx, headlineOf(code), W - 160).slice(0, 2).forEach((l, i) => ctx.fillText(l, W / 2, 190 + i * 104));
  // The code on a dashed ticket
  ctx.font = `700 64px ui-monospace, Menlo, monospace`;
  const cw = ctx.measureText(code.code).width + 96;
  ctx.setLineDash([16, 12]);
  ctx.lineWidth = 5;
  ctx.strokeStyle = INK;
  ctx.strokeRect((W - cw) / 2, 350, cw, 110);
  ctx.setLineDash([]);
  ctx.fillText(code.code, W / 2, 430);
  const qr = document.createElement('canvas');
  await QR.toCanvas(qr, url, { width: SIZE, margin: 0, errorCorrectionLevel: 'M', color: { dark: INK, light: '#ffffff' } });
  ctx.drawImage(qr, (W - SIZE) / 2, 530, SIZE, SIZE);
  ctx.font = `700 54px ${FONT}`;
  ctx.fillText('Scan to book with the code', W / 2, 530 + SIZE + 90);
  ctx.font = `400 30px ${FONT}`;
  ctx.fillStyle = '#5b5a60';
  const small = termsOf(code, { long: true });
  wrap(ctx, small, W - 200).slice(0, 4).forEach((l, i) => ctx.fillText(l, W / 2, 530 + SIZE + 160 + i * 42));
  if (name) {
    ctx.font = `600 34px ${FONT}`;
    ctx.fillStyle = INK;
    ctx.fillText(name, W / 2, H - 60);
  }
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('No image'))), 'image/png'));
}

const copy = (text, said) => {
  const done = () => { hapticSuccess(); toast.success(said); };
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text).then(done).catch(() => toast.error('Couldn’t copy. Press and hold to copy it instead.'));
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.focus(); ta.select();
    document.execCommand('copy'); document.body.removeChild(ta);
    done();
  } catch (e) { toast.error('Couldn’t copy. Press and hold to copy it instead.'); }
  return Promise.resolve();
};

export default function ShareSheet({ code, onClose }) {
  const history = useHistory();
  const panel = useRef(null);
  const [qr, setQr] = useState(null);
  const [busy, setBusy] = useState(false);
  const url = useMemo(() => linkFor(code), [code]);
  const qrUrl = useMemo(() => linkFor(code, 'qr'), [code]);
  const name = businessName(code);
  const small = termsOf(code, { long: true });

  useEffect(() => {
    let alive = true;
    if (!qrUrl) return undefined;
    loadQr()
      .then((QR) => QR.toDataURL(qrUrl, { width: 320, margin: 0, errorCorrectionLevel: 'M', color: { dark: INK, light: '#ffffff' } }))
      .then((data) => { if (alive) setQr(data); })
      .catch(() => { if (alive) setQr(null); });
    return () => { alive = false; };
  }, [qrUrl]);

  const savePoster = async () => {
    hapticTick();
    setBusy(true);
    try {
      const blob = await posterBlob({ url: qrUrl, code, name });
      await saveFile({ blob, filename: `${code.code.toLowerCase()}-poster.png`, title: `${code.code} poster` });
    } catch (e) {
      toast.error('The poster couldn’t be made just now. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const go = (path, state) => {
    hapticTick();
    if (panel.current) panel.current.handleClose();
    setTimeout(() => history.push(path, state), 320);
  };

  const text = `${termsShort(code)} Book: ${url}`;

  return (
    <SlidePanel ref={panel} title={`Share ${code.code}`} onClose={onClose} width={480} className="dc-page dc-share-panel">
      <div className="dc-share">
        <div className="dc-share-code">
          <span className="dc-code dc-code--big">{code.code}</span>
          <p>{headlineOf(code)}</p>
          <button type="button" className="dc-btn" onClick={() => copy(code.code, 'Code copied')}><Icon.Copy size={15} />Copy code</button>
        </div>

        <div className="dc-field">
          <label className="dc-label" htmlFor="dc-share-link">Booking link with the code on</label>
          <div className="dc-copyrow">
            <input id="dc-share-link" className="dc-input" readOnly value={url} onFocus={(e) => e.target.select()} />
            <button type="button" className="dc-btn" onClick={() => copy(url, 'Link copied')}>Copy</button>
          </div>
          <p className="dc-help">Opens your booking page with {code.code} already applied. It’s remembered for 14 days if they come back later.</p>
        </div>

        <div className="dc-qr">
          <div className="dc-qr-img">{qr ? <img src={qr} alt={`QR code for ${code.code}`} /> : <span className="dc-help">…</span>}</div>
          <div className="dc-qr-text">
            <b style={{ fontSize: 14, color: '#29282D' }}>QR poster</b>
            <p>For the window, the mirror or the till. Scanning it opens booking with the code applied.</p>
            <button type="button" className="dc-btn" onClick={savePoster} disabled={busy || !qrUrl}><Icon.Download size={15} />{busy ? 'Making it…' : 'Save poster'}</button>
          </div>
        </div>

        <div className="dc-field">
          <span className="dc-label">Put it in</span>
          <div className="dc-actions">
            {SOCIAL_VISIBLE && (
              <button type="button" className="dc-action" onClick={() => go('/marketing-social', { create: true, type: 'offer', codeId: code.id })}>
                <Icon.Post /><b>A post</b><span>An offer post with the code and its small print</span>
              </button>
            )}
            <button type="button" className="dc-action" onClick={() => go('/marketing-campaigns', { compose: 'email', codeId: code.id })}>
              <Icon.Mail /><b>An email</b><span>An offer section with the code</span>
            </button>
            <button type="button" className="dc-action" onClick={() => go('/marketing-campaigns', { compose: 'sms', codeId: code.id })}>
              <Icon.Text /><b>A text</b><span>The code, its conditions and the link</span>
            </button>
          </div>
        </div>

        <div className="dc-field">
          <span className="dc-label">Small print</span>
          <div className="dc-terms">{small}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="dc-btn" onClick={() => copy(small, 'Small print copied')}><Icon.Copy size={15} />Copy small print</button>
            <button type="button" className="dc-btn" onClick={() => copy(text, 'Text copied')}><Icon.Copy size={15} />Copy as a text</button>
          </div>
          <p className="dc-help">Put the small print wherever you share the code. Clients need the conditions before they book.</p>
        </div>
      </div>
    </SlidePanel>
  );
}
